/**
 * AttachmentSection.jsx
 * Sprint 5 Phase 2 — File Attachment UI
 *
 * Reusable attachment component for both Task and Subtask contexts.
 *
 * Props:
 *   taskId      {number|string}  — required (parent task id)
 *   subtaskId   {number|string}  — optional; when set, uses subtask attachment API
 *   readOnly    {boolean}        — hides upload/delete when true (closed task/subtask)
 *   title       {string}         — section heading (default "Attachments")
 *
 * Features:
 *   • Fetch list on mount (lazy — does not re-fetch unless panel opens)
 *   • Upload via file picker + drag-and-drop (no extra dependency)
 *   • Download via authenticated endpoint (redirect for S3, stream for local)
 *   • Delete with confirmation (soft-delete on backend)
 *   • Image preview modal (loaded only when user requests it — no auto-load)
 *   • Loading / error / empty states
 *   • Frontend validation: file type + 20 MB limit (UX only; backend is authoritative)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

const ALLOWED_ACCEPT = [
  '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp',
  '.doc', '.docx', '.xls', '.xlsx', '.csv', '.zip',
].join(',');

const IMAGE_EXTS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp']);

// ── Helpers ───────────────────────────────────────────────────────────────────

function authHeaders() {
  const token = localStorage.getItem('jwt') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

function getExt(name = '') {
  return (name.match(/\.[^.]+$/) || [''])[0].toLowerCase();
}

function fileIcon(name = '') {
  const ext = getExt(name);
  if (IMAGE_EXTS.has(ext))                      return '🖼️';
  if (ext === '.pdf')                            return '📄';
  if (ext === '.doc'  || ext === '.docx')        return '📝';
  if (ext === '.xls'  || ext === '.xlsx'
   || ext === '.csv')                            return '📊';
  if (ext === '.zip')                            return '🗜️';
  return '📎';
}

function fileTypeLabel(name = '') {
  const ext = getExt(name);
  const map = {
    '.pdf': 'PDF', '.jpg': 'JPG', '.jpeg': 'JPEG',
    '.png': 'PNG', '.gif': 'GIF', '.webp': 'WEBP',
    '.doc': 'DOC', '.docx': 'DOCX', '.xls': 'XLS',
    '.xlsx': 'XLSX', '.csv': 'CSV', '.zip': 'ZIP',
  };
  return map[ext] || ext.replace('.', '').toUpperCase() || 'FILE';
}

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024)          return `${bytes} B`;
  if (bytes < 1024 * 1024)   return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return ''; }
}

function isImage(name = '') {
  return IMAGE_EXTS.has(getExt(name));
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AttachmentSection({
  taskId,
  subtaskId,
  readOnly = false,
  title = 'Attachments',
  currentUserId,
}) {
  // Determine which API to use
  const baseUrl = subtaskId
    ? `${API}/subtasks/${subtaskId}/attachments`
    : `${API}/tasks/${taskId}/attachments`;

  const [attachments, setAttachments] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [loadError,   setLoadError]   = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const [dragOver,    setDragOver]    = useState(false);
  const [deleteId,    setDeleteId]    = useState(null);   // attachment id awaiting confirm
  const [deleting,    setDeleting]    = useState(false);
  const [downloadingId, setDownloadingId] = useState(null); // attachment id being downloaded
  const [previewAtt,  setPreviewAtt]  = useState(null);  // attachment for image preview
  const [previewUrl,  setPreviewUrl]  = useState(null);  // object URL for preview
  const [previewLoading, setPreviewLoading] = useState(false);

  const fileInputRef = useRef();

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchAttachments = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await axios.get(baseUrl, { headers: authHeaders() });
      setAttachments(res.data || []);
    } catch (err) {
      const msg = err.response?.data?.error || 'Unable to load attachments.';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    if (!taskId && !subtaskId) return;
    fetchAttachments();
  }, [fetchAttachments, taskId, subtaskId]);

  // ── Validate before upload (UX only) ─────────────────────────────────────
  function validateFrontend(file) {
    const ext = getExt(file.name);
    const allowed = ALLOWED_ACCEPT.split(',');
    if (!allowed.includes(ext)) {
      toast.error(`File type "${ext}" is not allowed.`);
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Maximum allowed file size is 20 MB.');
      return false;
    }
    if (file.size === 0) {
      toast.error('Cannot upload an empty file.');
      return false;
    }
    return true;
  }

  // ── Upload ────────────────────────────────────────────────────────────────
  async function handleUpload(file) {
    if (!validateFrontend(file)) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(baseUrl, form, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`"${file.name}" uploaded successfully.`);
      await fetchAttachments();
    } catch (err) {
      const msg = err.response?.data?.error || 'Upload failed.';
      if (err.response?.status === 403) {
        toast.error('You do not have permission to upload this file.');
      } else if (err.response?.status === 400) {
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setUploading(false);
      // Reset file input so the same file can be re-selected after an error
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function onFileChange(e) {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  }

  // ── Drag-and-drop ─────────────────────────────────────────────────────────
  function onDragOver(e) {
    e.preventDefault();
    setDragOver(true);
  }
  function onDragLeave() {
    setDragOver(false);
  }
  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  }

  // ── Download ──────────────────────────────────────────────────────────────
  async function handleDownload(att) {
    setDownloadingId(att.id);
    try {
      const token = localStorage.getItem('jwt') || localStorage.getItem('token');
      const url   = `${baseUrl}/${att.id}/download`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        redirect: 'follow',
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        toast.error(body.error || 'Download failed.');
        return;
      }

      const blob       = await response.blob();
      const objectUrl  = URL.createObjectURL(blob);
      const a          = document.createElement('a');
      a.href           = objectUrl;
      a.download       = att.original_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────
  async function confirmDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await axios.delete(`${baseUrl}/${deleteId}`, { headers: authHeaders() });
      toast.success('Attachment deleted.');
      setAttachments(prev => prev.filter(a => a.id !== deleteId));
    } catch (err) {
      const msg = err.response?.data?.error || 'Delete failed.';
      if (err.response?.status === 403) {
        toast.error('You do not have permission to delete this attachment.');
      } else {
        toast.error(msg);
      }
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  }

  // ── Image preview — only loaded when user requests it ─────────────────────
  async function openPreview(att) {
    if (!isImage(att.original_name)) return;
    setPreviewAtt(att);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const token = localStorage.getItem('jwt') || localStorage.getItem('token');
      const url   = `${baseUrl}/${att.id}/download`;
      const res   = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        redirect: 'follow',
      });
      if (!res.ok) throw new Error('Failed to load preview');
      const blob = await res.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch {
      toast.error('Could not load image preview.');
      setPreviewAtt(null);
    } finally {
      setPreviewLoading(false);
    }
  }

  function closePreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewAtt(null);
    setPreviewLoading(false);
  }

  // ── Ownership check (frontend hint only — backend is authoritative) ────────
  function canDeleteAtt(att) {
    if (readOnly) return false;
    // If we don't know currentUserId, show delete and let backend decide
    if (!currentUserId) return true;
    return Number(att.uploaded_by) === Number(currentUserId);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-2">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-1 h-5 rounded-full bg-[#7AA33E] inline-block" />
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">{title}</h2>
          {attachments.length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
              {attachments.length}
            </span>
          )}
        </div>
        {/* Upload button (header shortcut) */}
        {!readOnly && (
          <button
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#7AA33E] text-white text-xs font-semibold hover:bg-[#6a9335] disabled:opacity-50 transition"
          >
            {uploading
              ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" /> Uploading…</>
              : '+ Add Attachment'}
          </button>
        )}
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className="flex items-center gap-2 py-8 justify-center text-gray-400 text-sm">
          <span className="w-4 h-4 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
          Loading attachments…
        </div>
      )}

      {/* ── Error ── */}
      {!loading && loadError && (
        <div className="py-6 text-center">
          <p className="text-sm text-red-500 mb-3">{loadError}</p>
          <button
            onClick={fetchAttachments}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-xs font-semibold hover:bg-gray-50 transition"
          >
            ↻ Retry
          </button>
        </div>
      )}

      {/* ── Attachment list ── */}
      {!loading && !loadError && attachments.length > 0 && (
        <div className="flex flex-col gap-2 mb-4">
          {attachments.map(att => (
            <div
              key={att.id}
              className="flex items-start gap-3 px-3 py-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-white transition"
            >
              {/* Icon — clickable for image preview */}
              <button
                type="button"
                title={isImage(att.original_name) ? 'Click to preview' : att.original_name}
                onClick={() => isImage(att.original_name) ? openPreview(att) : handleDownload(att)}
                className="text-2xl flex-shrink-0 leading-none hover:scale-110 transition-transform"
              >
                {fileIcon(att.original_name)}
              </button>

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-semibold text-gray-800 truncate"
                  title={att.original_name}
                >
                  {att.original_name}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {fileTypeLabel(att.original_name)} · {formatSize(att.file_size)}
                  {att.uploader_name && ` · ${att.uploader_name}`}
                  {att.created_at && ` · ${formatDate(att.created_at)}`}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* Preview button (images only) */}
                {isImage(att.original_name) && (
                  <button
                    onClick={() => openPreview(att)}
                    title="Preview"
                    className="px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 transition"
                  >
                    👁 Preview
                  </button>
                )}
                {/* Download */}
                <button
                  onClick={() => handleDownload(att)}
                  disabled={downloadingId === att.id}
                  title="Download"
                  className="px-2.5 py-1.5 rounded-lg bg-[#23283a] text-white text-xs font-semibold hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition flex items-center gap-1.5"
                >
                  {downloadingId === att.id ? (
                    <>
                      <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                      Downloading…
                    </>
                  ) : (
                    <>↓ Download</>
                  )}
                </button>
                {/* Delete (sender or privileged) */}
                {canDeleteAtt(att) && (
                  <button
                    onClick={() => setDeleteId(att.id)}
                    title="Delete"
                    className="px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 transition"
                  >
                    🗑
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && !loadError && attachments.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          <div className="text-4xl mb-2">📎</div>
          <p className="text-sm font-medium text-gray-500">No attachments yet</p>
          {!readOnly && (
            <p className="text-xs text-gray-400 mt-1">Drag a file here or click "+ Add Attachment" above</p>
          )}
        </div>
      )}

      {/* ── Drag & Drop zone (shown when not read-only) ── */}
      {!readOnly && (
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`mt-2 rounded-xl border-2 border-dashed transition cursor-pointer flex items-center justify-center py-4 text-sm gap-2 select-none ${
            dragOver
              ? 'border-[#7AA33E] bg-[#f0f7e8] text-[#4a7a20]'
              : 'border-gray-200 text-gray-400 hover:border-[#7AA33E] hover:text-[#7AA33E]'
          } ${uploading ? 'pointer-events-none opacity-50' : ''}`}
        >
          {uploading ? (
            <>
              <span className="w-4 h-4 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
              Uploading…
            </>
          ) : (
            <>📂 Drop file here or <span className="font-semibold underline">browse</span></>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_ACCEPT}
        className="hidden"
        onChange={onFileChange}
      />

      {/* ── Read-only notice ── */}
      {readOnly && (
        <p className="text-xs text-gray-400 text-center mt-2">
          🔒 Attachments are read-only — the task is closed.
        </p>
      )}

      {/* ── Delete confirmation modal ── */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9998] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-bold text-gray-900 mb-2">Delete Attachment</h3>
            <p className="text-sm text-gray-500 mb-5">
              Are you sure you want to delete this attachment? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-50 transition"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Image preview modal ── */}
      {previewAtt && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4"
          onClick={closePreview}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Preview header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
              <p className="text-sm font-semibold text-gray-800 truncate" title={previewAtt.original_name}>
                {previewAtt.original_name}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(previewAtt)}
                  disabled={downloadingId === previewAtt?.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#23283a] text-white text-xs font-semibold hover:bg-gray-800 disabled:opacity-60 transition"
                >
                  {downloadingId === previewAtt?.id ? (
                    <>
                      <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                      Downloading…
                    </>
                  ) : (
                    <>↓ Download</>
                  )}
                </button>
                <button
                  onClick={closePreview}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 text-lg"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Preview body */}
            <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-100 min-h-[300px]">
              {previewLoading ? (
                <div className="flex flex-col items-center gap-3 text-gray-400 py-16">
                  <span className="w-8 h-8 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm">Loading preview…</span>
                </div>
              ) : previewUrl ? (
                <img
                  src={previewUrl}
                  alt={previewAtt.original_name}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                />
              ) : (
                <p className="text-gray-400 text-sm">Could not load preview.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
